/**
 * FederalOpsCard.js — 4-in-1 operational surface built Feb 24, 2026.
 *
 *   1. UEI renewal countdown banner (45-day alert)
 *   2. SAM.gov sync trigger + last-run status
 *   3. Certification progress ladder (Kanban-style buckets)
 *   4. Federal contract win ledger with per-NAICS revenue rollup
 *
 * Mount directly below FederalProfileCard inside CertificationsTab.
 */
import React, { useEffect, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CYAN = "#00CFCF";
const CHROME = "#A0A0A0";
const INK = "#F5F5F7";
const GREEN = "#22c55e";
const AMBER = "#F59E0B";
const RED = "#ef4444";

const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(16px,3vw,26px)", marginBottom: 20 };
const subLabel = { fontSize: 10, letterSpacing: "0.14em", color: CHROME, textTransform: "uppercase", marginBottom: 6, display: "block", fontWeight: 700 };
const btn = { background: CYAN, color: "#020A1F", border: 0, padding: "8px 14px", fontWeight: 800, borderRadius: 6, cursor: "pointer", fontSize: 12, letterSpacing: "0.04em" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, borderRadius: 6, padding: "7px 9px", color: INK, fontSize: 12, fontFamily: "inherit" };

const LADDER_COLORS = {
  not_started: CHROME,
  docs_ready:  "#7dd3fc",
  submitted:   AMBER,
  in_review:   "#a78bfa",
  awarded:     GREEN,
  rejected:    RED,
};

const KNOWN_CERTS = [
  { code: "TX-HUB",           label: "TX HUB" },
  { code: "SBA-WOSB",         label: "SBA WOSB" },
  { code: "SBA-8a",           label: "SBA 8(a)" },
  { code: "EDWOSB",           label: "EDWOSB" },
  { code: "WBENC",            label: "WBENC" },
  { code: "NMSDC",            label: "NMSDC" },
  { code: "HUD-Section-3",    label: "HUD Section 3" },
  { code: "HUD-VASH",         label: "HUD-VASH" },
  { code: "DFW-MSDC",         label: "DFW MSDC" },
  { code: "Dallas-BID-BEP",   label: "Dallas BID/BEP" },
  { code: "FortWorth-MWBE",   label: "Fort Worth M/WBE" },
  { code: "TUCP-DBE",         label: "TUCP DBE" },
];

export default function FederalOpsCard() {
  const [renewal, setRenewal] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [certRows, setCertRows] = useState([]);
  const [ladder, setLadder] = useState([]);
  const [labels, setLabels] = useState({});
  const [wins, setWins] = useState({ rows: [], by_naics: [], total_revenue_usd: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // New-win form
  const [newWin, setNewWin] = useState({
    contract_id: "", agency: "", solicitation_id: "",
    naics_code: "541990", award_amount_usd: 0, notes: "",
  });

  const loadAll = useCallback(async () => {
    setError("");
    try {
      const [rn, ss, cp, wn] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/federal/renewal-status`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/admin/federal/sam-sync/status`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/admin/federal/cert-progress`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/admin/federal/wins`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
      ]);
      if (rn) setRenewal(rn);
      if (ss) setSyncStatus(ss);
      if (cp) { setCertRows(cp.rows || []); setLadder(cp.ladder || []); setLabels(cp.labels || {}); }
      if (wn) setWins(wn);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ─── 1 · Renewal banner ─────────────────────────────────────────
  const renewalBanner = () => {
    if (!renewal || renewal.renewal_status === "unknown") return null;
    const s = renewal.renewal_status;
    const color = s === "expired" ? RED : (s === "renewal_window" ? AMBER : GREEN);
    const label = s === "expired" ? "EXPIRED" : (s === "renewal_window" ? "RENEWAL WINDOW OPEN" : "REGISTRATION ACTIVE");
    return (
      <div data-testid="uei-renewal-banner" style={{
        background: `rgba(${s === "expired" ? "239,68,68" : s === "renewal_window" ? "245,158,11" : "34,197,94"},0.12)`,
        border: `1px solid ${color}`,
        borderRadius: 8, padding: "10px 14px", marginBottom: 16,
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", fontSize: 12,
      }}>
        <span style={{ color, fontWeight: 800, letterSpacing: "0.08em" }}>UEI · {label}</span>
        <span style={{ color: INK }}>
          {renewal.uei ? <><strong>{renewal.uei}</strong> · </> : null}
          {renewal.days_remaining !== null && renewal.days_remaining >= 0
            ? <>{renewal.days_remaining} days until renewal · expires {new Date(renewal.expires_at).toLocaleDateString()}</>
            : renewal.expires_at
              ? <>Expired {new Date(renewal.expires_at).toLocaleDateString()} — renew at SAM.gov immediately</>
              : "Renewal window not computed yet."}
        </span>
      </div>
    );
  };

  // ─── 2 · SAM sync trigger ───────────────────────────────────────
  const triggerSync = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/federal/sam-sync/trigger`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Trigger failed (${r.status})`);
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // ─── 3 · Cert progress ladder ───────────────────────────────────
  const setCertStatus = async (cert_code, status) => {
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/federal/cert-progress`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cert_code, status }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Save failed (${r.status})`);
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const certByCode = React.useMemo(() => {
    const m = {};
    certRows.forEach(r => { m[r.cert_code] = r; });
    return m;
  }, [certRows]);

  // ─── 4 · Wins ledger ────────────────────────────────────────────
  const addWin = async () => {
    if (!newWin.contract_id.trim()) { setError("Contract ID required"); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/federal/wins`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newWin,
          award_amount_usd: Number(newWin.award_amount_usd) || 0,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Save failed (${r.status})`);
      setNewWin({ contract_id: "", agency: "", solicitation_id: "", naics_code: "541990", award_amount_usd: 0, notes: "" });
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const deleteWin = async (contract_id) => {
    if (!window.confirm(`Delete win ${contract_id}?`)) return;
    setBusy(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/federal/wins/${encodeURIComponent(contract_id)}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      await loadAll();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={card} data-testid="federal-ops-card">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, fontWeight: 800, marginBottom: 14 }}>
        FEDERAL OPS · SYNC · CERTS · WINS · RENEWAL
      </div>

      {renewalBanner()}

      {/* SAM sync */}
      <div style={{ marginBottom: 22 }}>
        <span style={subLabel}>SAM.gov Auto-Sync (nightly · 24h cadence)</span>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="sam-sync-trigger-btn" style={btn} onClick={triggerSync} disabled={busy}>
            {busy ? "Running…" : "▶ Run Sync Now"}
          </button>
          {syncStatus && (
            <div style={{ fontSize: 11, color: CHROME }}>
              solicitations: <strong style={{ color: INK }}>{syncStatus.total_solicitations}</strong>
              {" · "}proposals drafted: <strong style={{ color: INK }}>{syncStatus.total_proposals_drafted}</strong>
              {syncStatus.last_manual_run && <> · last manual: {new Date(syncStatus.last_manual_run.started_at).toLocaleString()}</>}
              {syncStatus.last_nightly_run && <> · last nightly: {new Date(syncStatus.last_nightly_run.started_at).toLocaleString()}</>}
            </div>
          )}
        </div>
      </div>

      {/* Cert ladder */}
      <div style={{ marginBottom: 22 }} data-testid="cert-progress-ladder">
        <span style={subLabel}>Certification Progress Ladder</span>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
          {KNOWN_CERTS.map(c => {
            const row = certByCode[c.code];
            const status = row?.status || "not_started";
            const color = LADDER_COLORS[status] || CHROME;
            return (
              <div key={c.code} data-testid={`cert-row-${c.code}`} style={{
                background: "rgba(0,0,0,0.28)",
                border: `1px solid ${color}66`,
                borderRadius: 6, padding: "8px 10px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <strong style={{ color: INK, fontSize: 12 }}>{c.label}</strong>
                  <span style={{ color, fontSize: 10, fontWeight: 800, letterSpacing: "0.05em" }}>
                    {(labels[status] || status).toUpperCase()}
                  </span>
                </div>
                <select
                  data-testid={`cert-status-${c.code}`}
                  style={{ ...input, padding: "4px 6px", fontSize: 11 }}
                  value={status}
                  onChange={e => setCertStatus(c.code, e.target.value)}>
                  {ladder.map(s => <option key={s} value={s}>{labels[s] || s}</option>)}
                </select>
                {row?.submitted_at && <div style={{ fontSize: 10, color: CHROME, marginTop: 3 }}>Submitted {new Date(row.submitted_at).toLocaleDateString()}</div>}
                {row?.awarded_at && <div style={{ fontSize: 10, color: GREEN, marginTop: 3 }}>Awarded {new Date(row.awarded_at).toLocaleDateString()}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Wins ledger */}
      <div style={{ marginBottom: 12 }} data-testid="federal-wins-ledger">
        <span style={subLabel}>Federal Contract Win Ledger</span>

        {/* Rollup */}
        {wins.by_naics.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div style={{ background: "rgba(0,207,207,0.14)", border: `1px solid ${CYAN}66`, padding: "8px 14px", borderRadius: 6, fontSize: 12 }}>
              <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.08em" }}>TOTAL REVENUE</div>
              <div style={{ color: CYAN, fontWeight: 800, fontSize: 16 }}>${wins.total_revenue_usd.toLocaleString()}</div>
            </div>
            {wins.by_naics.map(n => (
              <div key={n.naics_code} data-testid={`naics-rollup-${n.naics_code}`}
                   style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${CHROME}44`, padding: "8px 14px", borderRadius: 6, fontSize: 12 }}>
                <div style={{ fontSize: 10, color: CHROME }}>NAICS {n.naics_code}</div>
                <div style={{ color: INK, fontWeight: 700 }}>${Number(n.total_usd).toLocaleString()}</div>
                <div style={{ fontSize: 10, color: CHROME }}>{n.award_count} contract{n.award_count !== 1 ? "s" : ""} · {n.active_count} active</div>
              </div>
            ))}
          </div>
        )}

        {/* Add new win */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px auto", gap: 8, marginBottom: 12 }}>
          <input data-testid="win-contract-id" style={input} placeholder="Contract # / ID"
                 value={newWin.contract_id} onChange={e => setNewWin({...newWin, contract_id: e.target.value})} />
          <input data-testid="win-agency" style={input} placeholder="Agency (e.g. HUD)"
                 value={newWin.agency} onChange={e => setNewWin({...newWin, agency: e.target.value})} />
          <input data-testid="win-solicitation" style={input} placeholder="Solicitation ID"
                 value={newWin.solicitation_id} onChange={e => setNewWin({...newWin, solicitation_id: e.target.value})} />
          <input data-testid="win-amount" style={input} type="number" placeholder="Award $"
                 value={newWin.award_amount_usd} onChange={e => setNewWin({...newWin, award_amount_usd: e.target.value})} />
          <button data-testid="win-add-btn" style={btnGhost} onClick={addWin} disabled={busy}>+ Log</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, marginBottom: 12 }}>
          <input data-testid="win-naics" style={input} placeholder="NAICS code" maxLength={6}
                 value={newWin.naics_code} onChange={e => setNewWin({...newWin, naics_code: e.target.value.replace(/\D/g, "")})} />
          <input data-testid="win-notes" style={input} placeholder="Notes (optional)"
                 value={newWin.notes} onChange={e => setNewWin({...newWin, notes: e.target.value})} />
        </div>

        {/* Rows */}
        {wins.rows.length === 0 ? (
          <div style={{ color: CHROME, fontSize: 12, padding: "8px 0" }}>No wins logged yet — your first federal award posts here.</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {wins.rows.map(w => (
              <div key={w.contract_id} data-testid={`win-row-${w.contract_id}`}
                   style={{ background: "rgba(0,0,0,0.25)", border: `1px solid ${CHROME}33`, borderRadius: 6, padding: "8px 12px", display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center", fontSize: 12 }}>
                <div>
                  <strong style={{ color: INK }}>{w.contract_id}</strong>
                  {w.agency && <span style={{ color: CHROME }}> · {w.agency}</span>}
                  {w.naics_code && <span style={{ color: CYAN, marginLeft: 6, fontSize: 10 }}>NAICS {w.naics_code}</span>}
                  {w.notes && <div style={{ fontSize: 11, color: CHROME, marginTop: 3 }}>{w.notes}</div>}
                </div>
                <div style={{ color: GREEN, fontWeight: 800 }}>${Number(w.award_amount_usd || 0).toLocaleString()}</div>
                <button data-testid={`win-delete-${w.contract_id}`} onClick={() => deleteWin(w.contract_id)}
                        style={{ background: "transparent", border: 0, color: RED, cursor: "pointer", fontSize: 14 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ color: RED, fontSize: 12, marginTop: 8 }} data-testid="fed-ops-err">⚠ {error}</div>}
    </div>
  );
}
